/**
 * Background branch save (contracts 0.2.35): the save POST answers `200` (the save body, as before) or `202`
 * `BranchSaveAccepted` (told apart by HTTP status in `api.saveBranch`); for a 202 this runs the bounded poll loop and
 * settles into EXACTLY what the synchronous route would have produced: a resolved `SaveBranchOut`, or a thrown
 * `LaneV2Error` built from the poll's `result` + `http_status`, so the panel has ONE place that renders an outcome.
 * What a poll body means lives in `branchSave.ts` (pure).
 *
 * The transport, the clock and the abort signal are injected, so the loop is unit-tested without timers.
 */
import { isLaneV2Error, LaneV2Error, capacityWaitMs, type SaveBranchPost } from "./api";
import { classifyPoll, nextPollDelayMs } from "./branchSave";
import type { BranchSavePoll, SaveBranchOut } from "./types";

/** The save stopped before finishing (server restart / worker lost). Rows already on the branch may be partial. */
export class BranchSaveInterruptedError extends Error {
  readonly chunksWritten: number;
  constructor(chunksWritten: number) {
    super("branch save interrupted");
    this.name = "BranchSaveInterruptedError";
    this.chunksWritten = chunksWritten;
  }
}

/** The poll route answered 404: the save record is gone (expired or never ours). */
export class BranchSaveGoneError extends Error {
  constructor() {
    super("branch save record gone");
    this.name = "BranchSaveGoneError";
  }
}

export interface BranchSaveRunDeps {
  post: () => Promise<SaveBranchPost>;
  poll: (saveId: string) => Promise<BranchSavePoll>;
  /** Resolves after `ms`, or rejects with an AbortError-like when `signal` aborts. */
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
  signal?: AbortSignal;
  /** Called with the save id once the server answered 202 (before the first poll). */
  onAccepted?: (saveId: string) => void;
  /** Called with `chunks_written` while running (0 right after the 202). */
  onProgress?: (chunksWritten: number) => void;
  /** Injected for tests; defaults to `Date.now`. */
  now?: () => number;
}

/** The error a failed save would have thrown had the route answered synchronously. */
function errorFromResult(status: number, code: string | null, result: unknown): LaneV2Error {
  return new LaneV2Error(status, code, `branch save → ${status}${code ? ` ${code}` : ""}`, result);
}

async function postWithBusyRetry(deps: BranchSaveRunDeps): Promise<SaveBranchPost> {
  try {
    return await deps.post();
  } catch (err) {
    if (!isLaneV2Error(err) || err.code !== "BRANCH_SAVE_BUSY") throw err;
    // Honour Retry-After once, automatically; a second BUSY surfaces to the panel (which offers a retry).
    const wait = capacityWaitMs(err.retryAfterMs);
    await deps.sleep(wait, deps.signal);
    return deps.post();
  }
}

/** Polls `saveId` until it is no longer running; returns the save body or throws what the sync route would have. */
export async function pollBranchSave(deps: Omit<BranchSaveRunDeps, "post" | "onAccepted">, saveId: string): Promise<SaveBranchOut> {
  for (let attempt = 0; ; attempt++) {
    await deps.sleep(nextPollDelayMs(attempt), deps.signal);
    if (deps.signal?.aborted) throw new DOMException("aborted", "AbortError");
    let poll: BranchSavePoll;
    try {
      poll = await deps.poll(saveId);
    } catch (err) {
      if (deps.signal?.aborted || isAbortError(err)) throw err;
      if (isLaneV2Error(err)) {
        if (err.status === 404) throw new BranchSaveGoneError();
        // Any other 4xx ends tracking; a 5xx is a blip on the poll hop, the save runs on.
        if (err.status >= 400 && err.status < 500) throw err;
      }
      // 5xx / network error: keep polling (bounded backoff). Only the server's state ends a save, never a client timer.
      continue;
    }
    const outcome = classifyPoll(poll);
    if (outcome.kind === "running") {
      deps.onProgress?.(outcome.chunksWritten);
      continue;
    }
    if (outcome.kind === "interrupted") throw new BranchSaveInterruptedError(outcome.chunksWritten);
    if (outcome.kind === "failed") throw errorFromResult(outcome.status, outcome.code, outcome.result);
    return outcome.out;
  }
}

export async function runBranchSave(deps: BranchSaveRunDeps): Promise<SaveBranchOut> {
  const first = await postWithBusyRetry(deps);
  if (first.kind === "done") return first.out;
  deps.onAccepted?.(first.accepted.save_id);
  deps.onProgress?.(0);
  return pollBranchSave(deps, first.accepted.save_id);
}

/** Real sleep honouring an abort signal. */
export function abortableSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("aborted", "AbortError"));
    let t: ReturnType<typeof setTimeout> | undefined;
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("aborted", "AbortError"));
    };
    t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function isAbortError(err: unknown): boolean {
  return !!err && typeof err === "object" && (err as { name?: string }).name === "AbortError";
}
